import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MediaApi } from './MediaApi';
import { S3FileUploader } from '@/features/upload/S3FileUploader';
import { ensureValidCredentials } from '@/features/upload/useUploadCredentials';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { MEDIA_QUERY_KEYS, DEFAULT_POSTER_SIZE, DEFAULT_THUMB_SIZE } from '@/lib/constants/media';
import type { MediaPosterIngestRequest } from '@/lib/types/Media';

interface UploadPosterParams {
  file: File;
  contentType: string;
}

/**
 * Upload a poster image: create ingest record → upload to S3.
 * Returns the ingestJobId for precise job matching in usePosterJobPolling.
 */
export function useUploadPoster(mediaId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, contentType }: UploadPosterParams) => {
      if (!selectedCId) throw new Error('No customer selected');

      const req: MediaPosterIngestRequest = {
        downloadUrl: null,
        filename: file.name,
        ingestMethodType: 'dashboard',
        format: 'webp',
        poster: DEFAULT_POSTER_SIZE,
        thumb: DEFAULT_THUMB_SIZE,
      };

      // 1. Create poster ingest record → get S3 key
      const clientIngest = await MediaApi.createPoster(selectedCId, mediaId, req);

      if (!clientIngest.dashboard) {
        throw new Error('Backend returned no dashboard ingest data');
      }

      const { key, ingestJobId } = clientIngest.dashboard;

      // 2. Upload file to S3
      const credentials = await ensureValidCredentials(selectedCId);
      await S3FileUploader.putObject(credentials, key, file, contentType);

      return ingestJobId;
    },
    onSuccess: () => {
      if (!selectedCId) return;
      queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEYS.video(selectedCId, mediaId) });
      queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEYS.videos(selectedCId) });
    },
  });
}

/**
 * Delete poster and thumbnail.
 */
export function useDeletePoster(mediaId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!selectedCId) throw new Error('No customer selected');
      await MediaApi.deletePoster(selectedCId, mediaId);
    },
    onSuccess: () => {
      if (!selectedCId) return;
      queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEYS.video(selectedCId, mediaId) });
      queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEYS.videos(selectedCId) });
    },
  });
}
